import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box
} from '@mui/material';
import {
  SwitchStyled,
  SelectionBoxStyled,
  SelectionTitleStyled
} from '../style';
import { useAppSelector } from '../../../../store/hooks';
import criteriaIcon from '../../../../assets/icons/criteriaIcon.svg';
import bmiMeasureIcon from '../../../../assets/icons/bmiMeasureIcon.svg';

import muscleMass from '../../../../assets/icons/muscleMass.svg';
import { setGlim } from '../../../../store/slices/metaDataSlice';
import { metaDataSelector } from '../../../../store/globalSelectors';
import { PlaceholderGrid, CutOffIcon } from '../../../../styles/globalStyles';

import { CalfCircumference } from '../calfCircum';
import { EtilogicalCriteria } from '../etilogicalCriteria';

export const PhenoCriteria = () => {
  const dispatch = useDispatch();
  const {
    malnutritionScreening: { glim }
  } = useAppSelector(metaDataSelector);

  const option1 = 'BMI within range';
  const option2 =
    '<70 years with BMI <20kg/m\u00B2 or <18.5kg/m\u00B2 if from Asia';
  const option3 =
    '>70 years with BMI <22kg/m\u00B2 or <20kg/m\u00B2 if from Asia';
  const options = [
    { label: option1, value: 'one' },
    {
      label: option2,
      value: 'two'
    },
    { label: option3, value: 'three' }
  ];

  const handleBmiChange = (event) => {
    dispatch(
      setGlim({
        ...glim,
        bmiOption: event.target.value
      })
    );
  };

  return (
    <Box sx={{ padding: '0.9375rem' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <CutOffIcon>
          <img src={criteriaIcon} alt="glim" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <Typography
            sx={{
              fontSize: '1.125rem',
              lineHeight: '1.5rem',
              textAlign: 'center',
              paddingTop: '1.25rem',
              textTransform: 'uppercase',
              color: '#002A3A',
              fontFamily: 'BGBold'
            }}
          >
            Phenotypic criteria
          </Typography>
          <SelectionBoxStyled>
            <SelectionTitleStyled variant="h4">
              Has there been {'>'}5% weight loss within the past 6 months or{' '}
              {'>'}10% weight loss beyond 6 months?
            </SelectionTitleStyled>
            <SwitchStyled
              checked={glim.phenoCrtiteria}
              onChange={(event: any) =>
                dispatch(
                  setGlim({
                    ...glim,
                    phenoCrtiteria: event.target.checked
                  })
                )
              }
              inputProps={{
                'aria-label': 'Switch-Toggle',
                role: 'checkbox'
              }}
              data-testid="phenoCriteriaSwitch"
            />
          </SelectionBoxStyled>
        </PlaceholderGrid>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <CutOffIcon>
          <img src={bmiMeasureIcon} alt="bmimeasure" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <Typography
            sx={{
              fontSize: '1.125rem',
              lineHeight: '1.5rem',
              textAlign: 'center',
              paddingTop: '1.25rem',
              textTransform: 'uppercase',
              color: '#002A3A',
              fontFamily: 'BGBold'
            }}
          >
            Body Mass Index
          </Typography>

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup value={glim.bmiOption} onChange={handleBmiChange}>
              {options.map((option) => (
                <Box
                  key={option.label}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    padding: '0.5rem'
                  }}
                >
                  <FormControlLabel
                    value={option.value}
                    control={
                      <Radio
                        icon={
                          <Box
                            sx={{
                              borderRadius: '50%',
                              width: 14,
                              height: 14,
                              border: '0.0625rem solid #D9D9D6'
                            }}
                          />
                        }
                        checkedIcon={
                          <Box
                            sx={{
                              borderRadius: '50%',
                              width: 14,
                              height: 14,
                              backgroundColor: '#004F71'
                            }}
                          />
                        }
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: 'Calibri',
                          fontWeight: '400',
                          fontSize: '1rem',
                          lineHeight: '1.5rem'
                        }}
                        variant="body1"
                      >
                        {option.label}
                      </Typography>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </PlaceholderGrid>
      </Box>
      <CalfCircumference />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <CutOffIcon>
          <img src={muscleMass} alt="glim" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <Typography
            sx={{
              fontSize: '1.125rem',
              lineHeight: '1.5rem',
              textAlign: 'center',
              paddingTop: '1.25rem',
              textTransform: 'uppercase',
              color: '#002A3A',
              fontFamily: 'BGBold'
            }}
          >
            REDUCED MUSCLE MASS
          </Typography>
          <SelectionBoxStyled>
            <SelectionTitleStyled variant="h4">
              Does patient have reduced muscle mass?
            </SelectionTitleStyled>
            <SwitchStyled
              checked={glim.reduceMuscleMass}
              data-testid="reduceMuscleMassSwitch"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(
                  setGlim({
                    ...glim,
                    reduceMuscleMass: event.target.checked
                  })
                )
              }
              inputProps={{ 'aria-label': 'Switch-Toggle', role: 'checkbox' }}
            />
          </SelectionBoxStyled>
        </PlaceholderGrid>
      </Box>
      <EtilogicalCriteria />
    </Box>
  );
};
